import { useState } from "react";
import { closeOverlay, issueRecoveryCode, openOverlay, rename, syncNow, updateSettings } from "../../app/actions.ts";
import { shallowEqual, useApp } from "../../app/store.ts";
import { BUILD } from "../../env.ts";
import { companionVi } from "../../i18n/companion.vi.ts";
import { vi } from "../../i18n/vi.ts";
import { platform } from "../../services/platform.ts";
import { adsVi } from "../../i18n/ads.vi.ts";
import { PrivacySettings } from "../ads/AdsUi.tsx";
import { Art, Button, LanguageSwitch, QrCode, Sheet, Toggle } from "../components/common.tsx";
import { RestoreDialog } from "./Intro.tsx";
import { NativeUpdates } from "./Updates.tsx";

export function Settings() {
  const { save, outbox, sync, online, durable } = useApp(
    (s) => ({ save: s.save, outbox: s.outbox, sync: s.ui.sync, online: s.ui.online, durable: s.ui.durableSave }),
    shallowEqual,
  );
  const [name, setName] = useState(save?.profile.displayName ?? "");
  const [code, setCode] = useState<string | null>(null);
  const [showQr, setShowQr] = useState(false);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [restoring, setRestoring] = useState(false);
  if (!save) return null;
  const pending = outbox.filter((entry) => entry.status === "pending").length;
  const rejected = outbox.filter((entry) => entry.status === "rejected").length;

  const backup = async () => {
    setBusy(true);
    const issued = await issueRecoveryCode();
    setBusy(false);
    if (issued) setCode(issued);
  };

  return (
    <Sheet title={vi.settings.title} onClose={() => closeOverlay()} className="settings" testId="settings">
      <section className="settings__group">
        <div className="settings__row">
          <span>{vi.settings.language}</span>
          <LanguageSwitch />
        </div>
        <Toggle label={vi.settings.sound} checked={save.settings.sound} onChange={(sound) => void updateSettings({ sound })} />
        <Toggle label={vi.settings.music} checked={save.settings.music} onChange={(music) => void updateSettings({ music })} />
        <Toggle label={vi.settings.vibration} checked={save.settings.vibration} onChange={(vibration) => void updateSettings({ vibration })} />
        <Toggle
          label={companionVi.settingLabel}
          note={companionVi.settingNote}
          checked={save.settings.companion !== false}
          onChange={(companion) => void updateSettings({ companion })}
        />
      </section>

      {/* Sideloaded APKs check for updates here; web players (iPhones aside) can get the APK. The Play app updates through Play. */}
      {platform.native() && BUILD.store === "play" ? null : platform.native() ? (
        <section className="settings__group" data-testid="settings-updates">
          <h3>{vi.update.title}</h3>
          <NativeUpdates />
        </section>
      ) : !platform.ios() ? (
        <section className="settings__group" data-testid="settings-app">
          <h3>{vi.settings.app}</h3>
          <p className="note">{vi.settings.getApkNote}</p>
          <Button variant="gold" onClick={() => openOverlay("get-app")} testId="open-get-app">
            <Art id="icon.download" className="btn__icon" />
            {vi.settings.getApk}
          </Button>
        </section>
      ) : null}

      <section className="settings__group">
        <label className="field">
          <span>{vi.settings.name}</span>
          <div className="field__row">
            <input value={name} maxLength={20} onChange={(event) => setName(event.target.value)} data-testid="name-input" />
            <Button variant="secondary" onClick={() => void rename(name)} testId="name-save">
              {vi.settings.save}
            </Button>
          </div>
        </label>
        <Button variant="secondary" onClick={() => openOverlay("pick-change")}>
          {vi.settings.character}
        </Button>
      </section>

      <section className="settings__group">
        <h3>{vi.settings.backup}</h3>
        <p className="note">{vi.settings.backupBody}</p>
        {code ? (
          <div className="recovery" data-testid="recovery-code">
            <strong>{vi.settings.codeTitle}</strong>
            <code className="recovery__code">{code}</code>
            <p className="note">{vi.settings.codeOnce}</p>
            <div className="recovery__actions">
              <Button
                variant="secondary"
                onClick={() => {
                  void navigator.clipboard?.writeText(code).then(() => setCopied(true));
                }}
              >
                {copied ? vi.settings.copied : vi.settings.copy}
              </Button>
              <Button variant="ghost" onClick={() => setShowQr((value) => !value)}>
                {vi.settings.qr}
              </Button>
            </div>
            {showQr ? <QrCode text={code} /> : null}
          </div>
        ) : (
          <Button onClick={() => void backup()} disabled={busy || !online} testId="backup">
            {save.server?.hasRecoveryCode ? vi.settings.newCode : vi.settings.backup}
          </Button>
        )}
        {!online ? <p className="note">{vi.settings.backupNeedSync}</p> : null}
        <Button variant="ghost" onClick={() => setRestoring(true)} testId="open-restore">
          {vi.settings.restore}
        </Button>
        <p className="note">{vi.settings.honest}</p>
      </section>

      <section className="settings__group">
        <h3>{vi.settings.sync}</h3>
        <p data-testid="sync-state">
          <Art id={sync === "offline" || sync === "disabled" ? "icon.cloud-sleep" : "icon.cloud"} className="ico-inline" />
          {vi.settings.syncState[sync]}
          {pending > 0 ? `. ${vi.settings.pendingCount(pending)}` : ""}
        </p>
        {rejected > 0 ? <p className="note">{vi.settings.rejectedCount(rejected)}</p> : null}
        {!durable ? <p className="note">{vi.settings.storageBlocked}</p> : null}
        <Button variant="ghost" onClick={() => syncNow()} disabled={!online}>
          {vi.settings.sync}
        </Button>
      </section>

      <PrivacySettings />

      <section className="settings__group settings__about">
        <h3>{vi.settings.about}</h3>
        <p className="credit credit--strong">{vi.credit}</p>
        <p className="note">{vi.settings.privacy}</p>
        <p className="note">
          {vi.settings.version(BUILD.version)}
          {save.server?.publicTag ? `. ${vi.settings.tag(save.server.publicTag)}` : ""}
        </p>
        {BUILD.store === "play" ? <p className="note">{adsVi.privacy.updates}</p> : null}
      </section>
      {restoring ? <RestoreDialog onClose={() => setRestoring(false)} /> : null}
    </Sheet>
  );
}
